import type { PackageJsonESM, PackageJsonWebPage, ProxyConfig } from '@shared/wippy-package/wippy-package'
import type { AxiosInstance } from 'axios'
import type { WippyComponent } from '@/wippy-component-wrapper'
import { importMap, replaceProxyUrls, scripts } from '@shared/api/web-components/constants'
import { defaultPageEndpoints, makeArtifactApi } from '@shared/api/web-components/make-artifact-api'
import { getFromCache, putInCache } from '@shared/api/web-components/web-cache'
import { getAppConfig } from '@shared/app-config'
import { buildChildPayload } from '@shared/app-config/migration'
import { GLOBAL_CONFIG_VAR, GLOBAL_PROXY_CONFIG_VAR } from '@shared/constants/global'
import { uuid } from '@shared/util/uuid'
import { WIPPY_SCRIPTS_DATA_ROLE } from '@wippy-fe/shared'

export async function getWebComponentStartUrl(name: string, entry: string, api: AxiosInstance, baseUrlOverride?: string): Promise<string> {
  if (/^https?:\/\//.test(entry))
    return entry
  const baseUrl = baseUrlOverride || await makeArtifactApi(api).getBaseUrl(name)
  return `${baseUrl}/${entry}`
}
export function validateWippyComponent(something: unknown): something is WippyComponent {
  return typeof (something as WippyComponent).webComponent === 'function'
}

export async function loadESMComponentByPackageJSON(
  { uuid: name, packageJson: json }: { uuid: string, packageJson: PackageJsonESM },
  api: AxiosInstance,
) {
  const cacheName = `${name}:${json.version}`
  let tagName = getFromCache(cacheName)

  if (tagName) {
    return tagName
  }
  if (json.wippy.tagName && customElements.get(json.wippy.tagName)) {
    putInCache(cacheName, json.wippy.tagName, false)
    return json.wippy.tagName
  }
  const startUrl = await getWebComponentStartUrl(name, json.browser, api, json.baseUrl)
  const component = (await import(startUrl))

  if (!validateWippyComponent(component)) {
    throw new Error(`Invalid Wippy component: imported component not matching required structure.`)
  }

  if (!component.webComponent) {
    throw new Error(`Invalid Wippy component: webComponent function is required.`)
  }
  const Component = await component.webComponent()
  tagName = `wippy-${name}-${uuid()}`
  customElements.define(tagName, Component)
  putInCache(cacheName, tagName, false) // Browser has no ability to undeclare custom elements, so cache for them is not clearable

  return tagName
}

export async function importESMComponentByPackageJSON(
  { uuid: name, packageJson: json }: { uuid: string, packageJson: PackageJsonESM },
  tagName: string | undefined,
  api: AxiosInstance,
) {
  const newTagName = tagName || json.wippy.tagName
  const cacheName = `${name}:${json.version}:${newTagName}`
  if (getFromCache(cacheName)) {
    return newTagName
  }
  // if custom element with newTagName already exists, return it
  if (customElements.get(newTagName)) {
    throw new Error(`Custom element with tag name "${newTagName}" already exists. Please use a different tag name for ${name} component.`)
  }
  const startUrl = await getWebComponentStartUrl(name, json.browser, api, json.baseUrl)
  const componentUrl = new URL(startUrl)
  componentUrl.searchParams.set('declare-tag', tagName || json.wippy.tagName)

  const scriptEl = document.createElement('script')
  scriptEl.setAttribute('type', 'module')
  scriptEl.setAttribute('src', componentUrl.toString())
  document.head.appendChild(scriptEl)

  putInCache(cacheName, newTagName, false) // Browser has no ability to undeclare custom elements, so cache for them is not clearable

  return tagName
}

export async function addBaseUrlToDocument(doc: Document, baseUrl: string) {
  // Normalize double slashes in path (but preserve protocol://)
  const normalized = baseUrl.replace(/([^:])\/{2,}/g, '$1/')
  const baseElement = doc.querySelector('base')
  if (baseElement) {
    baseElement.setAttribute('href', normalized)
  }
  else {
    const newBase = doc.createElement('base')
    newBase.setAttribute('href', normalized)
    doc.head.insertBefore(newBase, doc.head.firstChild)
  }
}

export async function addImportMapToDocument(doc: Document, importMap: { imports: Record<string, string> }) {
  // Add base href as modulePath
  // Read the original import map from the HTML
  const originalImportMapScript = doc.querySelector('script[type="importmap"]')
  let originalImportMap = { imports: {} }
  if (originalImportMapScript && originalImportMapScript.textContent) {
    try {
      originalImportMap = JSON.parse(originalImportMapScript.textContent)
    }
    catch (error) {
      console.warn('Failed to parse original import map:', error)
    }
  }

  // Merge with custom import map
  const mergedImportMap = {
    imports: {
      ...originalImportMap.imports,
      ...(importMap?.imports || {}),
    },
  }

  // Update or create the import map in the parsed HTML
  let importMapScript = doc.querySelector('script[type="importmap"]')
  if (!importMapScript) {
    importMapScript = doc.createElement('script')
    importMapScript.setAttribute('type', 'importmap')
    doc.head.insertBefore(importMapScript, doc.head.firstChild)
  }
  importMapScript.textContent = JSON.stringify(mergedImportMap, null, 8)
}

export async function addAppConfigToDocument(doc: Document, appConfig: any, proxyConfig?: ProxyConfig, scripts: string[] = [], forceAppConfig: boolean = false) {
  // Find the @wippy/scripts element and add custom scripts before it
  let wippyScriptsElement = doc.querySelector(`script[data-role="${WIPPY_SCRIPTS_DATA_ROLE}"]`)
  if (!wippyScriptsElement && forceAppConfig) {
    // If not found, create a new script element
    wippyScriptsElement = doc.createElement('script')
    wippyScriptsElement.setAttribute('type', 'text/javascript')
    wippyScriptsElement.setAttribute('data-role', WIPPY_SCRIPTS_DATA_ROLE)
    doc.head.appendChild(wippyScriptsElement)
  }
  if (wippyScriptsElement) {
    if (appConfig) {
      const appConfigScript = doc.createElement('script')
      appConfigScript.setAttribute('type', 'text/javascript')
      appConfigScript.setAttribute('data-role', 'wippy-app-config')
      appConfigScript.textContent = `
        window.${GLOBAL_CONFIG_VAR} = ${JSON.stringify(appConfig)};
      `
      wippyScriptsElement.parentNode?.insertBefore(appConfigScript, wippyScriptsElement)
    }
    if (proxyConfig) {
      const proxyConfigScript = doc.createElement('script')
      proxyConfigScript.setAttribute('type', 'text/javascript')
      proxyConfigScript.setAttribute('data-role', 'wippy-proxy-config')
      proxyConfigScript.textContent = `
        window.${GLOBAL_PROXY_CONFIG_VAR} = ${JSON.stringify(proxyConfig, undefined, 2)};
      `
      wippyScriptsElement.parentNode?.insertBefore(proxyConfigScript, wippyScriptsElement)
    }
    // Add custom scripts before the @wippy/scripts element
    scripts.forEach((src) => {
      const script = doc.createElement('script')
      script.setAttribute('src', src)
      // script.setAttribute('type', 'module')
      script.setAttribute('data-role', 'wippy-proxy-script')
      wippyScriptsElement.parentNode?.insertBefore(script, wippyScriptsElement)
    })

    // Remove the @wippy/scripts element
    wippyScriptsElement.remove()
  }
}

export async function processWebPage(pageHTML: string, options: {
  scripts?: string[]
  baseUrl?: string
  appConfig?: any
  proxyConfig?: ProxyConfig
  importMap?: { imports: Record<string, string> }
  forceAppConfig?: boolean
} = {}) {
  const { baseUrl, importMap, scripts = [] } = options

  // Replace deprecated front.wippy.ai proxy URLs with current proxy URL
  pageHTML = replaceProxyUrls(pageHTML)

  // Parse the HTML to extract and modify content
  const parser = new DOMParser()
  const doc = parser.parseFromString(pageHTML, 'text/html')

  if (baseUrl) {
    // Add base URL to the document
    await addBaseUrlToDocument(doc, baseUrl)
  }

  if (importMap) {
    // Add import map to the document
    await addImportMapToDocument(doc, importMap)
  }

  await addAppConfigToDocument(
    doc,
    options.appConfig,
    options.proxyConfig,
    scripts,
    options.forceAppConfig || false,
  )

  // Ensure import map is the first script in <head>. Vite's build can
  // reorder scripts so the import map ends up after proxy.js. proxy.js
  // (UMD classic script) uses dynamic import() with bare specifiers that
  // need the import map to be registered before execution. The import map
  // is pure metadata (no code execution) so placing it first does not
  // delay proxy.js or affect the API setup ordering.
  const importMapEl = doc.querySelector('script[type="importmap"]')
  if (importMapEl && importMapEl.parentNode) {
    const firstScript = doc.querySelector('head script')
    if (firstScript && firstScript !== importMapEl)
      importMapEl.parentNode.insertBefore(importMapEl, firstScript)
  }

  // Return the modified HTML as string
  return doc.documentElement.outerHTML
}

export async function loadWebPageByPackageJson(
  { uuid: name, packageJson: json, initialRoute }: { uuid: string, packageJson: PackageJsonWebPage, initialRoute?: string },
  api: AxiosInstance,
) {
  const INITIAL_ROUTE_PLACEHOLDER = '__WIPPY_INITIAL_ROUTE__PLACEHOLDER__'
  const QUOTED_PLACEHOLDER = JSON.stringify(INITIAL_ROUTE_PLACEHOLDER)

  function normalizeInitialRoute(route?: string): string {
    if (!route || route === '')
      return '/'
    return route.startsWith('/') ? route : `/${route}`
  }

  function applyInitialRouteToSrcdoc(srcdoc: string, route?: string): string {
    const idx = srcdoc.indexOf(QUOTED_PLACEHOLDER)
    if (idx === -1)
      return srcdoc

    const encoded = JSON.stringify(normalizeInitialRoute(route))
    return srcdoc.slice(0, idx) + encoded + srcdoc.slice(idx + QUOTED_PLACEHOLDER.length)
  }

  // Cache the processed srcdoc WITHOUT initialRoute in the key. The cached
  // srcdoc uses a placeholder for `context.route`, and we replace it at read time.
  const cacheName = `${name}:${json.version}`
  const cachedTemplate = getFromCache(cacheName)
  if (cachedTemplate)
    return applyInitialRouteToSrcdoc(cachedTemplate, initialRoute)
  const htmlPath = json.wippy.path || 'index.html'
  // Calc dir from htmlPath
  const htmlDir = htmlPath.includes('/') ? htmlPath.replace(/\/[^/]+$/, '') : ''

  let baseUrl: string
  let content: string
  if (json.baseUrl) {
    // baseUrl already in package JSON — fetch HTML directly, skip public URL request
    baseUrl = json.baseUrl.replace(/\/$/, '')
    const fullHtmlFilePath = `${baseUrl}/${htmlPath.replace(/^\//, '')}`
    content = (await api.get(fullHtmlFilePath)).data
    baseUrl = `${baseUrl}/`
  }
  else {
    const result = await makeArtifactApi(api, defaultPageEndpoints).getBaseContent(name, htmlPath)
    baseUrl = result.baseUrl
    content = result.content
  }
  const fullHtmlDirPath = htmlDir ? `${baseUrl}${htmlDir}/` : baseUrl

  const appConfigForChild = buildChildPayload(
    getAppConfig(),
    {
      resourceId: name,
      resourceType: 'page',
      route: INITIAL_ROUTE_PLACEHOLDER,
    },
    json.wippy.configOverrides,
  )
  const srcdoc = await processWebPage(
    content,
    {
      scripts,
      importMap,
      appConfig: appConfigForChild,
      baseUrl: fullHtmlDirPath,
      proxyConfig: json.wippy.proxy,
    },
  )
  putInCache(cacheName, srcdoc, true)

  return applyInitialRouteToSrcdoc(srcdoc, initialRoute)
}
